/**
 * Visitor Identification Page
 * Track anonymous website visitors, identify companies via IP enrichment,
 * and manage the prospect queue for lead generation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import {
  Users,
  Building2,
  Eye,
  Search,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  ExternalLink,
  Globe,
  Clock,
  TrendingUp,
  Target,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  Code,
  Copy,
  Check,
  Loader2,
  BarChart3,
  UserPlus,
  Trash2,
  MapPin,
  Briefcase,
  Mail,
  Activity,
  Zap,
  Settings,
  Key,
  DollarSign,
  Server,
  Shield,
  Network,
  Database,
  ToggleLeft,
  ToggleRight,
  Save,
  TestTube,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  visitorIdentificationApi,
  Visitor,
  VisitorCompany,
  ProspectQueueItem,
  TrackingSnippet,
  VisitorAnalytics,
  VisitorStatus,
  CompanyStatus,
  QueueType,
  EnrichmentProvider,
  EnrichmentAnalytics,
  EnrichmentResult,
  IPMapping,
} from '../../shared/services/platformApi';
import { tvTokens, TvPageBar, TvButton } from '../../shared/components/triveny';
import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

type PageTab = 'companies' | 'visitors' | 'queue' | 'analytics' | 'enrichment' | 'setup';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function getStatusColor(status: string): string {
  switch (status) {
    case 'discovered': return 'bg-blue-100 text-blue-800';
    case 'reviewing': return 'bg-yellow-100 text-yellow-800';
    case 'prospect': return 'bg-purple-100 text-purple-800';
    case 'qualified': return 'bg-green-100 text-green-800';
    case 'customer': return 'bg-emerald-100 text-emerald-800';
    case 'ignored': return 'bg-gray-100 text-gray-800';
    case 'anonymous': return 'bg-gray-100 text-gray-600';
    case 'identified': return 'bg-blue-100 text-blue-800';
    case 'converted': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-600';
  }
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-green-600 bg-green-100';
  if (score >= 60) return 'text-yellow-600 bg-yellow-100';
  if (score >= 40) return 'text-orange-600 bg-orange-100';
  return 'text-red-600 bg-red-100';
}

function formatTimeAgo(dateString: string | null | undefined): string {
  if (!dateString) return 'Never';

  const date = new Date(dateString);
  // Check for invalid date (e.g., epoch date from null/empty)
  if (isNaN(date.getTime()) || date.getTime() < 86400000) return 'Never';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}

// ============================================================================
// TRACKING SNIPPET MODAL
// ============================================================================

interface TrackingSnippetModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function TrackingSnippetModal({ isOpen, onClose }: TrackingSnippetModalProps) {
  const [snippet, setSnippet] = useState<TrackingSnippet | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && !snippet) {
      setLoading(true);
      visitorIdentificationApi.getTrackingSnippet().then(response => {
        if (response.success && response.data) {
          setSnippet(response.data);
        }
        setLoading(false);
      });
    }
  }, [isOpen, snippet]);

  const handleCopy = () => {
    if (snippet) {
      navigator.clipboard.writeText(snippet.snippet_html);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code className="h-5 w-5" />
            Tracking Snippet
          </DialogTitle>
          <DialogDescription>
            Add this code to your website to start tracking visitors.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : snippet ? (
          <div className="space-y-4 py-4">
            <div className="relative">
              <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                <code>{snippet.snippet_html}</code>
              </pre>
              <button
                onClick={handleCopy}
                className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded text-gray-300"
              >
                {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Installation Instructions</h4>
              <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
                <li>Copy the tracking snippet above</li>
                <li>Paste it into the {'<head>'} section of every page</li>
                <li>The script will automatically track page views and engagement</li>
              </ol>
            </div>

            <div className="bg-gray-50 border rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Custom Event Tracking</h4>
              <pre className="bg-white border rounded p-3 text-sm overflow-x-auto">
{`// Track a button click
wiTrack('track', 'button_click', {
  event_category: 'engagement',
  event_label: 'signup_button'
});

// Identify a visitor
wiTrack('identify', {
  email: 'user@example.com',
  firstName: 'John',
  company: 'Acme Inc'
});`}
              </pre>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">
            Failed to load tracking snippet
          </div>
        )}

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
          {snippet && (
            <button
              onClick={handleCopy}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied!' : 'Copy Snippet'}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// COMPANY DETAIL MODAL
// ============================================================================

interface CompanyDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: VisitorCompany | null;
  onStatusChange: (companyId: string, status: CompanyStatus) => void;
  onAddToQueue: (companyId: string, queueType: QueueType) => void;
  onCompanyUpdate?: (company: VisitorCompany) => void;
}

function CompanyDetailModal({ isOpen, onClose, company, onStatusChange, onAddToQueue, onCompanyUpdate }: CompanyDetailModalProps) {
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    company_name: '',
    domain: '',
    industry: '',
  });
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && company) {
      setLoading(true);
      setIsEditing(false);
      setActionError(null);
      setActionSuccess(null);
      setEditForm({
        company_name: company.company_name || '',
        domain: company.domain || '',
        industry: company.industry || '',
      });
      visitorIdentificationApi.getCompanyVisitors(company.id).then(response => {
        if (response.success && response.data) {
          setVisitors(response.data);
        }
        setLoading(false);
      });
    }
  }, [isOpen, company]);

  const handleSaveEdit = async () => {
    if (!company) return;
    setSaving(true);
    setActionError(null);
    try {
      const response = await visitorIdentificationApi.updateCompany(company.id, editForm);
      if (response.success && response.data) {
        setIsEditing(false);
        setActionSuccess('Company updated successfully');
        if (onCompanyUpdate) {
          onCompanyUpdate(response.data);
        }
        setTimeout(() => setActionSuccess(null), 3000);
      } else {
        setActionError(response.error || 'Failed to update company');
      }
    } catch (err) {
      setActionError('Failed to update company');
    }
    setSaving(false);
  };

  const handleAddToQueueWithFeedback = async (queueType: QueueType) => {
    if (!company) return;
    setActionError(null);
    try {
      const response = await visitorIdentificationApi.addToProspectQueue(company.id, queueType);
      if (response.success) {
        setActionSuccess(`Added to ${queueType.replace('_', ' ')} queue`);
        setTimeout(() => setActionSuccess(null), 3000);
        onAddToQueue(company.id, queueType);
      } else {
        setActionError(response.error || 'Failed to add to queue');
      }
    } catch (err) {
      setActionError('Failed to add to queue');
    }
  };

  if (!company) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {isEditing ? (
              <input
                type="text"
                value={editForm.company_name}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                className="px-2 py-1 border rounded text-lg font-semibold flex-1"
                placeholder="Company Name"
              />
            ) : (
              <div className="flex items-center justify-between w-full">
                <span>{company.company_name}</span>
                <button
                  onClick={() => setIsEditing(true)}
                  className="ml-4 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center gap-1 transition-colors"
                  title="Edit company details"
                >
                  <Settings className="h-4 w-4" /> Edit
                </button>
              </div>
            )}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? (
              <input
                type="text"
                value={editForm.domain}
                onChange={(e) => setEditForm({ ...editForm, domain: e.target.value })}
                className="px-2 py-1 border rounded text-sm w-full mt-1"
                placeholder="Domain (e.g., example.com)"
              />
            ) : (
              company.domain && (
                <a href={`https://${company.domain}`} target="_blank" rel="noopener noreferrer"
                   className="text-blue-600 hover:underline flex items-center gap-1">
                  {company.domain} <ExternalLink className="h-3 w-3" />
                </a>
              )
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Action Feedback */}
        {actionError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg flex items-center gap-2">
            <XCircle className="h-4 w-4" />
            {actionError}
          </div>
        )}
        {actionSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            {actionSuccess}
          </div>
        )}

        {/* Edit Form Actions */}
        {isEditing && (
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                setIsEditing(false);
                setEditForm({
                  company_name: company.company_name || '',
                  domain: company.domain || '',
                  industry: company.industry || '',
                });
              }}
              className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={saving}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Changes
            </button>
          </div>
        )}

        <div className="space-y-6 py-4">
          {/* Company Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 uppercase">Industry</div>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.industry}
                  onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                  className="px-2 py-1 border rounded text-sm w-full"
                  placeholder="Industry"
                />
              ) : (
                <div className="font-medium">{company.industry || 'Unknown'}</div>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 uppercase">Size</div>
              <div className="font-medium">{company.employee_count_range || 'Unknown'}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 uppercase">Location</div>
              <div className="font-medium">
                {[company.headquarters_city, company.headquarters_state, company.headquarters_country]
                  .filter(Boolean).join(', ') || 'Unknown'}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 uppercase">Status</div>
              <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(company.status)}`}>
                {company.status}
              </span>
            </div>
          </div>

          {/* Scores */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Fit Score</span>
                <span className={`px-2 py-1 rounded text-sm font-semibold ${getScoreColor(company.fit_score)}`}>
                  {company.fit_score}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${company.fit_score}%` }} />
              </div>
            </div>
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Intent Score</span>
                <span className={`px-2 py-1 rounded text-sm font-semibold ${getScoreColor(company.intent_score)}`}>
                  {company.intent_score}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-purple-600 h-2 rounded-full" style={{ width: `${company.intent_score}%` }} />
              </div>
            </div>
          </div>

          {/* Activity Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{company.total_visitors}</div>
              <div className="text-sm text-gray-600">Visitors</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{company.total_visits}</div>
              <div className="text-sm text-gray-600">Visits</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{company.total_page_views}</div>
              <div className="text-sm text-gray-600">Page Views</div>
            </div>
          </div>

          {/* Recent Visitors */}
          <div>
            <h4 className="font-semibold mb-3">Recent Visitors</h4>
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : visitors.length > 0 ? (
              <div className="space-y-2">
                {visitors.slice(0, 5).map(visitor => (
                  <div key={visitor.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <Users className="h-4 w-4 text-gray-500" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">
                          {visitor.status === 'identified' ? 'Identified Visitor' : 'Anonymous Visitor'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {visitor.total_visits} visits · {visitor.total_page_views} pages
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{formatTimeAgo(visitor.last_seen_at)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500">No visitors yet</div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t">
            <select
              value={company.status}
              onChange={(e) => onStatusChange(company.id, e.target.value as CompanyStatus)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="discovered">Discovered</option>
              <option value="reviewing">Reviewing</option>
              <option value="prospect">Prospect</option>
              <option value="qualified">Qualified</option>
              <option value="customer">Customer</option>
              <option value="ignored">Ignored</option>
            </select>
            <button
              onClick={() => handleAddToQueueWithFeedback('review')}
              className="px-3 py-2 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700 flex items-center gap-1"
            >
              <Plus className="h-4 w-4" /> Add to Review Queue
            </button>
            <button
              onClick={() => handleAddToQueueWithFeedback('prospect_create')}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"
            >
              <UserPlus className="h-4 w-4" /> Create Prospect
            </button>
            <button
              onClick={() => handleAddToQueueWithFeedback('enrich')}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 flex items-center gap-1"
            >
              <Zap className="h-4 w-4" /> Enrich Data
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ENRICHMENT PROVIDER CONFIG MODAL
// ============================================================================

interface EnrichmentProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  provider: EnrichmentProvider | null;
  onSave: (providerName: string, data: Partial<EnrichmentProvider>) => void;
}

function EnrichmentProviderModal({ isOpen, onClose, provider, onSave }: EnrichmentProviderModalProps) {
  const [formData, setFormData] = useState({
    is_enabled: false,
    api_key: '',
    monthly_budget: 0,
    daily_request_limit: 0,
    monthly_request_limit: 0,
    confidence_threshold: 50,
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (provider) {
      setFormData({
        is_enabled: provider.is_enabled,
        api_key: '',
        monthly_budget: provider.monthly_budget || 0,
        daily_request_limit: provider.daily_request_limit || 0,
        monthly_request_limit: provider.monthly_request_limit || 0,
        confidence_threshold: provider.confidence_threshold || 50,
      });
      setTestResult(null);
    }
  }, [provider]);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    // Simulate API test
    await new Promise(resolve => setTimeout(resolve, 1500));
    if (formData.api_key) {
      setTestResult({ success: true, message: 'API connection successful!' });
    } else {
      setTestResult({ success: false, message: 'Please enter an API key to test.' });
    }
    setTesting(false);
  };

  const handleSave = () => {
    if (!provider) return;
    const data: Partial<EnrichmentProvider> = {
      is_enabled: formData.is_enabled,
      confidence_threshold: formData.confidence_threshold,
    };
    if (formData.api_key) {
      data.api_key_encrypted = formData.api_key; // Backend will encrypt
    }
    if (provider.tier === 'third_party') {
      data.monthly_budget = formData.monthly_budget;
      data.daily_request_limit = formData.daily_request_limit;
      data.monthly_request_limit = formData.monthly_request_limit;
    }
    onSave(provider.provider_name, data);
    onClose();
  };

  if (!provider) return null;

  const isThirdParty = provider.tier === 'third_party';
  const tierInfo = {
    native: { label: 'Native WeaveIn', color: 'bg-green-100 text-green-800', icon: Database },
    native_tools: { label: 'Native Tools', color: 'bg-blue-100 text-blue-800', icon: Network },
    third_party: { label: 'Third Party', color: 'bg-purple-100 text-purple-800', icon: Server },
  };
  const tier = tierInfo[provider.tier] || tierInfo.native;
  const TierIcon = tier.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configure {provider.display_name}
          </DialogTitle>
          <DialogDescription>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${tier.color}`}>
              <TierIcon className="h-3 w-3" />
              {tier.label}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium">Enable Provider</div>
              <div className="text-sm text-gray-500">
                Include this provider in enrichment lookups
              </div>
            </div>
            <button
              onClick={() => setFormData({ ...formData, is_enabled: !formData.is_enabled })}
              className={`p-1 rounded-full transition-colors ${formData.is_enabled ? 'bg-green-600' : 'bg-gray-300'}`}
            >
              {formData.is_enabled ? (
                <ToggleRight className="h-6 w-6 text-white" />
              ) : (
                <ToggleLeft className="h-6 w-6 text-white" />
              )}
            </button>
          </div>

          {/* Confidence Threshold */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confidence Threshold: {formData.confidence_threshold}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={formData.confidence_threshold}
              onChange={(e) => setFormData({ ...formData, confidence_threshold: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>Low (More Results)</span>
              <span>High (More Accurate)</span>
            </div>
          </div>

          {/* Third-party specific settings */}
          {isThirdParty && (
            <>
              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key {provider.api_key_set ? '(Already Set)' : '*'}
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="password"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={provider.api_key_set ? '••••••••••••' : 'Enter API key'}
                  />
                </div>
                {provider.api_key_set && (
                  <p className="text-xs text-gray-500 mt-1">Leave blank to keep existing key</p>
                )}
              </div>

              {/* Budget Settings */}
              <div className="space-y-4 p-4 border rounded-lg">
                <h4 className="font-medium flex items-center gap-2">
                  <DollarSign className="h-4 w-4" /> Budget Controls
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Budget ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="10"
                      value={formData.monthly_budget}
                      onChange={(e) => setFormData({ ...formData, monthly_budget: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="0 = unlimited"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost/Request ($)</label>
                    <input
                      type="text"
                      value={provider.cost_per_request?.toFixed(3) || '0.000'}
                      disabled
                      className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Daily Limit</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.daily_request_limit}
                      onChange={(e) => setFormData({ ...formData, daily_request_limit: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="0 = unlimited"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Limit</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.monthly_request_limit}
                      onChange={(e) => setFormData({ ...formData, monthly_request_limit: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="0 = unlimited"
                    />
                  </div>
                </div>
              </div>

              {/* Test Connection */}
              {testResult && (
                <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  <div className="flex items-center gap-2">
                    {testResult.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    {testResult.message}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Current Usage Stats */}
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Current Usage</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-blue-600 font-semibold">{provider.today_requests}</div>
                <div className="text-blue-700">Today</div>
              </div>
              <div>
                <div className="text-blue-600 font-semibold">{provider.current_month_requests}</div>
                <div className="text-blue-700">This Month</div>
              </div>
              {isThirdParty && (
                <div>
                  <div className="text-blue-600 font-semibold">${provider.current_month_cost.toFixed(2)}</div>
                  <div className="text-blue-700">Cost (MTD)</div>
                </div>
              )}
            </div>
            {provider.success_rate !== undefined && (
              <div className="mt-2 pt-2 border-t border-blue-200">
                <div className="flex justify-between text-sm">
                  <span className="text-blue-700">Success Rate</span>
                  <span className="text-blue-600 font-semibold">{(provider.success_rate * 100).toFixed(1)}%</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="pt-4">
          {isThirdParty && (
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testing}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube className="h-4 w-4" />}
              Test Connection
            </button>
          )}
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Save Configuration
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// IP LOOKUP MODAL
// ============================================================================

interface IPLookupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function IPLookupModal({ isOpen, onClose }: IPLookupModalProps) {
  const [ipAddress, setIpAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EnrichmentResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLookup = async () => {
    if (!ipAddress) return;
    setLoading(true);
    setResult(null);
    setError(null);

    const response = await visitorIdentificationApi.lookupIP(ipAddress);
    if (response.success && response.data) {
      setResult(response.data);
    } else {
      setError(response.error?.message || 'Lookup failed');
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Manual IP Lookup
          </DialogTitle>
          <DialogDescription>
            Test IP enrichment by looking up a specific IP address.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={ipAddress}
              onChange={(e) => setIpAddress(e.target.value)}
              className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter IP address (e.g., 8.8.8.8)"
            />
            <button
              onClick={handleLookup}
              disabled={loading || !ipAddress}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lookup'}
            </button>
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-800 rounded-lg flex items-center gap-2">
              <XCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          {result && (
            <div className="space-y-3">
              {result.success ? (
                <>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2 text-green-800 font-medium mb-2">
                      <CheckCircle className="h-5 w-5" />
                      Company Identified
                    </div>
                    <div className="text-lg font-semibold text-green-900">{result.company_name}</div>
                    {result.domain && (
                      <div className="text-sm text-green-700">{result.domain}</div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500">Industry</div>
                      <div className="font-medium">{result.industry || 'Unknown'}</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500">Size</div>
                      <div className="font-medium">{result.employee_count_range || 'Unknown'}</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500">Location</div>
                      <div className="font-medium">
                        {result.location ? [result.location.city, result.location.state, result.location.country].filter(Boolean).join(', ') : 'Unknown'}
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500">Confidence</div>
                      <div className={`font-medium ${result.confidence >= 70 ? 'text-green-600' : result.confidence >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {result.confidence}%
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Source:</span> {result.source}
                    {result.cost > 0 && <span className="ml-2">• Cost: ${result.cost.toFixed(3)}</span>}
                  </div>
                </>
              ) : (
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">No company found for this IP</span>
                  </div>
                  <div className="text-sm text-yellow-700 mt-1">
                    Providers attempted: {result.providers_attempted.join(', ')}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ADD IP MAPPING MODAL
// ============================================================================

interface AddIPMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  companies: VisitorCompany[];
  onSave: (data: { ip_address: string; company_id: string; notes?: string }) => void;
}

function AddIPMappingModal({ isOpen, onClose, companies, onSave }: AddIPMappingModalProps) {
  const [formData, setFormData] = useState({
    ip_address: '',
    company_id: '',
    notes: '',
  });

  const handleSave = () => {
    if (!formData.ip_address || !formData.company_id) return;
    onSave(formData);
    setFormData({ ip_address: '', company_id: '', notes: '' });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add IP Mapping
          </DialogTitle>
          <DialogDescription>
            Manually map an IP address to a company.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IP Address *</label>
            <input
              type="text"
              value={formData.ip_address}
              onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 192.168.1.1 or 192.168.1.0/24"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
            <select
              value={formData.company_id}
              onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a company...</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Optional notes about this mapping"
            />
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!formData.ip_address || !formData.company_id}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Add Mapping
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// VISITOR DETAIL MODAL
// ============================================================================

interface VisitorDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitor: Visitor | null;
  onAddToQueue: (visitorId: string, queueType: QueueType) => void;
  onLookupCompany: (visitor: Visitor) => void;
}

function VisitorDetailModal({ isOpen, onClose, visitor, onAddToQueue, onLookupCompany }: VisitorDetailModalProps) {
  const [timeline, setTimeline] = useState<{
    visits: Array<{
      id: string;
      session_id: string;
      started_at: string;
      ended_at?: string;
      duration_seconds?: number;
      ip_address: string;
      device_type?: string;
      browser?: string;
      referrer_type?: string;
      utm_source?: string;
      utm_campaign?: string;
      page_views: number;
      landing_page_url?: string;
    }>;
    page_views: Array<{
      id: string;
      page_url: string;
      page_path: string;
      page_title?: string;
      viewed_at: string;
      time_on_page_seconds?: number;
      scroll_depth_percent?: number;
    }>;
    events: Array<{
      id: string;
      event_category: string;
      event_action?: string;
      event_label?: string;
      event_value?: number;
      page_url?: string;
      occurred_at: string;
    }>;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTimelineTab, setActiveTimelineTab] = useState<'visits' | 'pages' | 'events'>('visits');

  useEffect(() => {
    if (isOpen && visitor) {
      setLoading(true);
      visitorIdentificationApi.getVisitorTimeline(visitor.id, 50).then(response => {
        if (response.success && response.data) {
          setTimeline(response.data);
        }
        setLoading(false);
      });
    }
  }, [isOpen, visitor]);

  if (!visitor) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Visitor Details
          </DialogTitle>
          <DialogDescription>
            {visitor.status === 'identified' ? 'Identified Visitor' : 'Anonymous Visitor'} - {visitor.visitor_fingerprint?.slice(0, 12)}...
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Status & Scores */}
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(visitor.status)}`}>
              {visitor.status}
            </span>
            <span className={`px-2 py-1 text-xs rounded ${getScoreColor(visitor.behavior_score)}`}>
              Behavior: {visitor.behavior_score}
            </span>
            <span className={`px-2 py-1 text-xs rounded ${getScoreColor(visitor.intent_score)}`}>
              Intent: {visitor.intent_score}
            </span>
          </div>

          {/* Visitor Info Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500 mb-2">IP Address</h4>
              <div className="font-mono text-sm">{visitor.last_ip || visitor.last_ip_address || '—'}</div>
              {visitor.first_ip_address && visitor.first_ip_address !== visitor.last_ip_address && (
                <div className="text-xs text-gray-400 mt-1">First: {visitor.first_ip_address}</div>
              )}
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Device</h4>
              <div className="text-sm capitalize">{visitor.last_device_type || '—'}</div>
              <div className="text-xs text-gray-500">
                {[visitor.last_browser, visitor.last_os].filter(Boolean).join(' / ') || '—'}
              </div>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Location</h4>
              {visitor.last_geo_city || visitor.last_geo_country ? (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="text-sm">
                    {[visitor.last_geo_city, visitor.last_geo_country].filter(Boolean).join(', ')}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-500 mb-2">Activity</h4>
              <div className="text-sm">{visitor.total_visits} visits</div>
              <div className="text-xs text-gray-500">{visitor.total_page_views} page views</div>
            </div>
          </div>

          {/* Company Card */}
          <div className="p-4 border rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Building2 className="h-4 w-4" /> Company
            </h4>
            {visitor.company_name ? (
              <div>
                <div className="font-medium text-blue-600">{visitor.company_name}</div>
                {visitor.company_confidence && (
                  <div className="text-xs text-gray-500 mt-1">
                    Confidence: {Math.round(visitor.company_confidence * 100)}%
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-gray-400">No company linked</span>
                {(visitor.last_ip || visitor.last_ip_address) && (
                  <button
                    onClick={() => onLookupCompany(visitor)}
                    className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex items-center gap-1"
                  >
                    <Search className="h-3 w-3" /> Lookup Company
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Timeline Tabs */}
          <div>
            <div className="flex gap-2 border-b">
              {(['visits', 'pages', 'events'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTimelineTab(tab)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTimelineTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {timeline && (
                    <span className="ml-1 text-xs text-gray-400">
                      ({tab === 'visits' ? timeline.visits.length : tab === 'pages' ? timeline.page_views.length : timeline.events.length})
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="mt-4 max-h-60 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : timeline ? (
                <>
                  {activeTimelineTab === 'visits' && (
                    <div className="space-y-2">
                      {timeline.visits.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">No visits recorded</div>
                      ) : (
                        timeline.visits.map(visit => (
                          <div key={visit.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                            <div className="flex justify-between">
                              <span className="font-mono text-xs">{visit.session_id?.slice(0, 8)}...</span>
                              <span className="text-gray-500">{formatTimeAgo(visit.started_at)}</span>
                            </div>
                            <div className="mt-1 text-gray-600">
                              {visit.page_views} pages • {visit.device_type} • {visit.browser}
                            </div>
                            {visit.landing_page_url && (
                              <div className="mt-1 text-xs text-gray-400 truncate">
                                Landing: {visit.landing_page_url}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {activeTimelineTab === 'pages' && (
                    <div className="space-y-2">
                      {timeline.page_views.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">No page views recorded</div>
                      ) : (
                        timeline.page_views.map(pv => (
                          <div key={pv.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                            <div className="flex justify-between">
                              <span className="font-medium truncate max-w-xs">{pv.page_title || pv.page_path}</span>
                              <span className="text-gray-500">{formatTimeAgo(pv.viewed_at)}</span>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">{pv.page_url}</div>
                            {(pv.time_on_page_seconds || pv.scroll_depth_percent) && (
                              <div className="text-xs text-gray-500 mt-1">
                                {pv.time_on_page_seconds && `${pv.time_on_page_seconds}s`}
                                {pv.scroll_depth_percent && ` • ${pv.scroll_depth_percent}% scroll`}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {activeTimelineTab === 'events' && (
                    <div className="space-y-2">
                      {timeline.events.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">No events recorded</div>
                      ) : (
                        timeline.events.map(event => (
                          <div key={event.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                            <div className="flex justify-between">
                              <span className="font-medium">{event.event_category}</span>
                              <span className="text-gray-500">{formatTimeAgo(event.occurred_at)}</span>
                            </div>
                            {event.event_action && (
                              <div className="text-gray-600">{event.event_action}</div>
                            )}
                            {event.event_label && (
                              <div className="text-xs text-gray-400">{event.event_label}</div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4 text-gray-500">No timeline data available</div>
              )}
            </div>
          </div>

          {/* Time Info */}
          <div className="text-xs text-gray-500 flex gap-4">
            <span>First seen: {formatTimeAgo(visitor.first_seen_at)}</span>
            <span>Last seen: {formatTimeAgo(visitor.last_seen_at)}</span>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => onAddToQueue(visitor.id, 'review')}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-1 text-sm"
            >
              <Plus className="h-4 w-4" /> Add to Queue
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// ENRICHMENT REVIEW MODAL
// ============================================================================

interface EnrichmentReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitor: Visitor | null;
  enrichmentResult: EnrichmentResult | null;
  onAccept: (data: { company_name: string; domain?: string; industry?: string }) => void;
  onSkip: () => void;
}

function EnrichmentReviewModal({ isOpen, onClose, visitor, enrichmentResult, onAccept, onSkip }: EnrichmentReviewModalProps) {
  const [editForm, setEditForm] = useState({
    company_name: '',
    domain: '',
    industry: '',
  });

  useEffect(() => {
    if (enrichmentResult) {
      setEditForm({
        company_name: enrichmentResult.company_name || '',
        domain: enrichmentResult.domain || '',
        industry: enrichmentResult.industry || '',
      });
    }
  }, [enrichmentResult]);

  if (!visitor || !enrichmentResult) return null;

  const handleAccept = () => {
    if (!editForm.company_name) return;
    onAccept({
      company_name: editForm.company_name,
      domain: editForm.domain || undefined,
      industry: editForm.industry || undefined,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Review Company Match
          </DialogTitle>
          <DialogDescription>
            Review and edit the company identification before linking to visitor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Source Info */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">Source: {enrichmentResult.source}</span>
              <span className={`px-2 py-1 text-xs rounded ${
                enrichmentResult.confidence >= 70 ? 'bg-green-100 text-green-800' :
                enrichmentResult.confidence >= 50 ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {enrichmentResult.confidence}% confidence
              </span>
            </div>
            {enrichmentResult.cost > 0 && (
              <div className="text-xs text-blue-700 mt-1">Cost: ${enrichmentResult.cost.toFixed(3)}</div>
            )}
          </div>

          {/* Visitor Info */}
          <div className="p-3 bg-gray-50 rounded-lg text-sm">
            <div className="text-gray-500">Visitor IP: <span className="font-mono">{visitor.last_ip || visitor.last_ip_address}</span></div>
          </div>

          {/* Editable Fields */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <input
                type="text"
                value={editForm.company_name}
                onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter company name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
              <input
                type="text"
                value={editForm.domain}
                onChange={(e) => setEditForm({ ...editForm, domain: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
              <input
                type="text"
                value={editForm.industry}
                onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Technology, Healthcare"
              />
            </div>
          </div>

          {/* Additional Enrichment Data */}
          {(enrichmentResult.employee_count_range || enrichmentResult.location) && (
            <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
              {enrichmentResult.employee_count_range && (
                <div>
                  <span className="text-gray-500">Employees:</span> {enrichmentResult.employee_count_range}
                </div>
              )}
              {enrichmentResult.location && (
                <div>
                  <span className="text-gray-500">Location:</span>{' '}
                  {[enrichmentResult.location.city, enrichmentResult.location.state, enrichmentResult.location.country].filter(Boolean).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <button
            onClick={onSkip}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
          >
            Skip
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAccept}
              disabled={!editForm.company_name}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
            >
              <CheckCircle className="h-4 w-4" /> Accept & Link
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export function VisitorIdentificationPage() {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const isTriveny = productConfig.isTriveny;
  const [activeTab, setActiveTab] = useState<PageTab>('companies');
  const [companies, setCompanies] = useState<VisitorCompany[]>([]);
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [queueItems, setQueueItems] = useState<ProspectQueueItem[]>([]);
  const [analytics, setAnalytics] = useState<VisitorAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<VisitorCompany | null>(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showSnippetModal, setShowSnippetModal] = useState(false);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [totalVisitors, setTotalVisitors] = useState(0);

  // Enrichment state
  const [enrichmentProviders, setEnrichmentProviders] = useState<EnrichmentProvider[]>([]);
  const [enrichmentAnalytics, setEnrichmentAnalytics] = useState<EnrichmentAnalytics | null>(null);
  const [ipMappings, setIpMappings] = useState<IPMapping[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<EnrichmentProvider | null>(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showIPLookupModal, setShowIPLookupModal] = useState(false);
  const [showAddMappingModal, setShowAddMappingModal] = useState(false);

  // Add to queue feedback state
  const [addingToQueue, setAddingToQueue] = useState<string | null>(null);
  const [addedToQueue, setAddedToQueue] = useState<string | null>(null);

  // Visitor detail modal state
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [showVisitorModal, setShowVisitorModal] = useState(false);

  // Enrichment review state
  const [showEnrichmentReviewModal, setShowEnrichmentReviewModal] = useState(false);
  const [enrichmentReviewVisitor, setEnrichmentReviewVisitor] = useState<Visitor | null>(null);
  const [enrichmentResult, setEnrichmentResult] = useState<EnrichmentResult | null>(null);
  const [lookingUpVisitor, setLookingUpVisitor] = useState<string | null>(null);

  // Visitor queue feedback state
  const [addingVisitorToQueue, setAddingVisitorToQueue] = useState<string | null>(null);
  const [addedVisitorToQueue, setAddedVisitorToQueue] = useState<string | null>(null);
  const [visitorActionError, setVisitorActionError] = useState<string | null>(null);

  // Fetch companies
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    const response = await visitorIdentificationApi.getCompanies({
      status: statusFilter as CompanyStatus || undefined,
      page: 1,
      page_size: 50,
    });
    if (response.success && response.data) {
      setCompanies(response.data.companies || []);
      setTotalCompanies(response.data.total || 0);
    }
    setLoading(false);
  }, [statusFilter]);

  // Fetch visitors
  const fetchVisitors = useCallback(async () => {
    setLoading(true);
    const response = await visitorIdentificationApi.getVisitors({
      page: 1,
      page_size: 50,
    });
    if (response.success && response.data) {
      setVisitors(response.data.visitors || []);
      setTotalVisitors(response.data.total || 0);
    }
    setLoading(false);
  }, []);

  // Fetch queue
  const fetchQueue = useCallback(async () => {
    setLoading(true);
    const response = await visitorIdentificationApi.getProspectQueue({
      status: 'pending',
      page: 1,
      page_size: 50,
    });
    if (response.success && response.data) {
      setQueueItems(response.data.items || []);
    }
    setLoading(false);
  }, []);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    const response = await visitorIdentificationApi.getAnalytics();
    if (response.success && response.data) {
      setAnalytics(response.data);
    }
    setLoading(false);
  }, []);

  // Fetch enrichment providers
  const fetchEnrichmentProviders = useCallback(async () => {
    const response = await visitorIdentificationApi.getEnrichmentProviders();
    if (response.success && response.data) {
      setEnrichmentProviders(response.data);
    }
  }, []);

  // Fetch enrichment analytics
  const fetchEnrichmentAnalytics = useCallback(async () => {
    const response = await visitorIdentificationApi.getEnrichmentAnalytics();
    if (response.success && response.data) {
      setEnrichmentAnalytics(response.data);
    }
  }, []);

  // Fetch IP mappings
  const fetchIPMappings = useCallback(async () => {
    const response = await visitorIdentificationApi.getIPMappings({ page_size: 100 });
    if (response.success && response.data) {
      setIpMappings(response.data.mappings || []);
    }
  }, []);

  // Fetch enrichment data
  const fetchEnrichmentData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchEnrichmentProviders(), fetchEnrichmentAnalytics(), fetchIPMappings()]);
    setLoading(false);
  }, [fetchEnrichmentProviders, fetchEnrichmentAnalytics, fetchIPMappings]);

  // Initial data load
  useEffect(() => {
    switch (activeTab) {
      case 'companies':
        fetchCompanies();
        break;
      case 'visitors':
        fetchVisitors();
        break;
      case 'queue':
        fetchQueue();
        break;
      case 'analytics':
        fetchAnalytics();
        break;
      case 'enrichment':
        fetchEnrichmentData();
        break;
    }
  }, [activeTab, fetchCompanies, fetchVisitors, fetchQueue, fetchAnalytics, fetchEnrichmentData]);

  // Handle company status change
  const handleStatusChange = async (companyId: string, status: CompanyStatus) => {
    const response = await visitorIdentificationApi.updateCompanyStatus(companyId, status);
    if (response.success) {
      fetchCompanies();
      if (selectedCompany && selectedCompany.id === companyId) {
        setSelectedCompany({ ...selectedCompany, status });
      }
    }
  };

  // Handle add to queue
  const handleAddToQueue = async (companyId: string, queueType: QueueType) => {
    setAddingToQueue(companyId);
    setAddedToQueue(null);
    try {
      const response = await visitorIdentificationApi.addToProspectQueue(companyId, queueType);
      if (response.success) {
        setAddedToQueue(companyId);
        fetchQueue();
        // Clear success indicator after 2 seconds
        setTimeout(() => setAddedToQueue(null), 2000);
      }
    } finally {
      setAddingToQueue(null);
    }
  };

  // Handle process queue item
  const handleProcessQueueItem = async (queueId: string, action: 'complete' | 'skip') => {
    const response = await visitorIdentificationApi.processQueueItem(queueId, action);
    if (response.success) {
      fetchQueue();
    }
  };

  // Handle visitor view details
  const handleViewVisitorDetails = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setShowVisitorModal(true);
  };

  // Handle visitor add to queue
  const handleAddVisitorToQueue = async (visitorId: string, queueType: QueueType) => {
    setAddingVisitorToQueue(visitorId);
    setAddedVisitorToQueue(null);
    setVisitorActionError(null);
    try {
      const response = await visitorIdentificationApi.addVisitorToQueue(visitorId, queueType);
      if (response.success) {
        setAddedVisitorToQueue(visitorId);
        fetchQueue();
        setTimeout(() => setAddedVisitorToQueue(null), 2000);
      } else {
        const errorMsg = response.error?.message || 'Failed to add visitor to queue';
        setVisitorActionError(errorMsg);
        console.error('Add to queue failed:', errorMsg);
        setTimeout(() => setVisitorActionError(null), 5000);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unexpected error adding to queue';
      setVisitorActionError(errorMsg);
      console.error('Add to queue error:', err);
      setTimeout(() => setVisitorActionError(null), 5000);
    } finally {
      setAddingVisitorToQueue(null);
    }
  };

  // Handle lookup company for visitor (WHOIS/DNS)
  const handleLookupVisitorCompany = async (visitor: Visitor) => {
    if (!visitor.last_ip && !visitor.last_ip_address) {
      setVisitorActionError('No IP address available for this visitor');
      setTimeout(() => setVisitorActionError(null), 5000);
      return;
    }

    setLookingUpVisitor(visitor.id);
    setVisitorActionError(null);
    const ipToLookup = visitor.last_ip || visitor.last_ip_address;

    try {
      const response = await visitorIdentificationApi.lookupIP(ipToLookup!);
      if (response.success && response.data) {
        // Check if enrichment found a company
        if (response.data.company_name) {
          setEnrichmentResult(response.data);
          setEnrichmentReviewVisitor(visitor);
          setShowEnrichmentReviewModal(true);
        } else {
          // No company found - show message with source info if available
          const msg = response.data.source?.startsWith('error:')
            ? `Lookup error: ${response.data.source.replace('error:', '').trim()}`
            : `No company found for IP ${ipToLookup}. Checked: ${response.data.providers_attempted?.join(', ') || 'reverse DNS, WHOIS'}`;
          setVisitorActionError(msg);
          setTimeout(() => setVisitorActionError(null), 5000);
        }
      } else {
        const errorMsg = response.error?.message || 'IP lookup failed';
        setVisitorActionError(errorMsg);
        console.error('WHOIS lookup failed:', errorMsg);
        setTimeout(() => setVisitorActionError(null), 5000);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unexpected error during lookup';
      setVisitorActionError(errorMsg);
      console.error('WHOIS lookup error:', err);
      setTimeout(() => setVisitorActionError(null), 5000);
    } finally {
      setLookingUpVisitor(null);
    }
  };

  // Handle link visitor to company (from enrichment review)
  const handleLinkVisitorToCompany = async (data: {
    company_name: string;
    domain?: string;
    industry?: string;
  }) => {
    if (!enrichmentReviewVisitor) return;

    const response = await visitorIdentificationApi.linkVisitorToCompany(
      enrichmentReviewVisitor.id,
      {
        ...data,
        from_enrichment: true,
        enrichment_source: enrichmentResult?.source,
        enrichment_confidence: enrichmentResult?.confidence,
      }
    );

    if (response.success) {
      setShowEnrichmentReviewModal(false);
      setEnrichmentReviewVisitor(null);
      setEnrichmentResult(null);
      fetchVisitors();
    }
  };

  // Handle skip enrichment review
  const handleSkipEnrichmentReview = async () => {
    if (!enrichmentReviewVisitor) return;

    await visitorIdentificationApi.skipVisitorReview(enrichmentReviewVisitor.id);
    setShowEnrichmentReviewModal(false);
    setEnrichmentReviewVisitor(null);
    setEnrichmentResult(null);
  };

  // Handle save enrichment provider
  const handleSaveProvider = async (providerName: string, data: Partial<EnrichmentProvider>) => {
    const response = await visitorIdentificationApi.updateEnrichmentProvider(providerName, data);
    if (response.success) {
      fetchEnrichmentProviders();
    }
  };

  // Handle add IP mapping
  const handleAddIPMapping = async (data: { ip_address: string; company_id: string; notes?: string }) => {
    const response = await visitorIdentificationApi.createIPMapping(data);
    if (response.success) {
      fetchIPMappings();
    }
  };

  // Handle delete IP mapping
  const handleDeleteIPMapping = async (mappingId: string) => {
    const response = await visitorIdentificationApi.deleteIPMapping(mappingId);
    if (response.success) {
      fetchIPMappings();
    }
  };

  // Filter companies by search
  const filteredCompanies = companies.filter(company =>
    company.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (company.domain && company.domain.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Filter visitors by search
  const filteredVisitors = visitors.filter(visitor =>
    visitor.visitor_fingerprint.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const visitorStats: Array<{ label: string; value: number | string; Icon: any }> = [
    { label: 'Companies identified', value: totalCompanies, Icon: Building2 },
    { label: 'Total visitors', value: totalVisitors, Icon: Users },
    { label: 'In queue', value: queueItems.length, Icon: Target },
    { label: 'High intent', value: companies.filter(c => c.intent_score >= 70).length, Icon: TrendingUp },
  ];
  const visitorTabs: PageTab[] = ['companies', 'visitors', 'queue', 'analytics', 'enrichment', 'setup'];

  return (
    <div
      className={isTriveny ? 'triveny-platform' : 'min-h-screen bg-gray-50'}
      style={
        isTriveny
          ? {
              minHeight: '100vh',
              background: tvTokens.paper,
              fontFamily: tvTokens.fontSans,
              color: tvTokens.ink,
            }
          : undefined
      }
    >
      {isTriveny ? (
        <>
          <TvPageBar
            eyebrow="Intelligence · Visitor ID"
            title={t('platform.visitorId.title', 'Visitor Identification')}
            actions={
              <TvButton variant="ink" icon={Code} onClick={() => setShowSnippetModal(true)}>
                {t('platform.visitorId.getTrackingCode', 'Get Tracking Code')}
              </TvButton>
            }
          />
          <div style={{ padding: '20px 22px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
                marginBottom: 18,
              }}
            >
              {visitorStats.map(({ label, value, Icon }) => (
                <div
                  key={label}
                  style={{
                    background: tvTokens.surface,
                    border: `1px solid ${tvTokens.border}`,
                    borderRadius: tvTokens.rLg,
                    padding: '14px 16px',
                    boxShadow: tvTokens.shadow1,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontFamily: tvTokens.fontMono,
                      fontSize: 10,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: tvTokens.ink500,
                      marginBottom: 6,
                    }}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </div>
                  <div
                    style={{
                      fontFamily: tvTokens.fontDisplay,
                      fontSize: 26,
                      fontWeight: 600,
                      letterSpacing: '-0.025em',
                      color: tvTokens.ink,
                    }}
                  >
                    {value}
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                gap: 4,
                marginBottom: 16,
                borderBottom: `1px solid ${tvTokens.borderMuted}`,
                overflowX: 'auto',
              }}
            >
              {visitorTabs.map(tab => {
                const active = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      padding: '8px 14px',
                      fontFamily: tvTokens.fontDisplay,
                      fontSize: 13,
                      fontWeight: 600,
                      color: active ? tvTokens.ink : tvTokens.ink500,
                      background: 'transparent',
                      border: 0,
                      borderBottom: `2px solid ${active ? tvTokens.accent : 'transparent'}`,
                      cursor: 'pointer',
                      transition: 'color .12s ease, border-color .12s ease',
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                    }}
                    aria-current={active ? 'page' : undefined}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                {!tabbed && (
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Eye className="h-6 w-6 text-blue-600" />
                  {t('platform.visitorId.title', 'Visitor Identification')}
                </h1>
                )}
                <p className="text-gray-500 mt-1">
                  {t('platform.visitorId.description', 'Track website visitors and identify companies through IP enrichment')}
                </p>
              </div>
              <button
                onClick={() => setShowSnippetModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Code className="h-4 w-4" />
                {t('platform.visitorId.getTrackingCode', 'Get Tracking Code')}
              </button>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <span className="text-sm text-gray-600">{t('platform.visitorId.companiesIdentified', 'Companies Identified')}</span>
                </div>
                <div className="text-2xl font-bold text-blue-600 mt-1">{totalCompanies}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-gray-600">{t('platform.visitorId.totalVisitors', 'Total Visitors')}</span>
                </div>
                <div className="text-2xl font-bold text-green-600 mt-1">{totalVisitors}</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-600" />
                  <span className="text-sm text-gray-600">{t('platform.visitorId.inQueue', 'In Queue')}</span>
                </div>
                <div className="text-2xl font-bold text-purple-600 mt-1">{queueItems.length}</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-orange-600" />
                  <span className="text-sm text-gray-600">{t('platform.visitorId.highIntent', 'High Intent')}</span>
                </div>
                <div className="text-2xl font-bold text-orange-600 mt-1">
                  {companies.filter(c => c.intent_score >= 70).length}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-1 mt-6 border-b">
              {(['companies', 'visitors', 'queue', 'analytics', 'enrichment', 'setup'] as PageTab[]).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === tab
                      ? 'bg-white text-blue-600 border border-b-white -mb-px'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search & Filter Bar */}
        {(activeTab === 'companies' || activeTab === 'visitors') && (
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {activeTab === 'companies' && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg"
              >
                <option value="">{t('platform.visitorId.allStatuses', 'All Statuses')}</option>
                <option value="discovered">{t('platform.visitorId.status.discovered', 'Discovered')}</option>
                <option value="reviewing">{t('platform.visitorId.status.reviewing', 'Reviewing')}</option>
                <option value="prospect">{t('platform.visitorId.status.prospect', 'Prospect')}</option>
                <option value="qualified">{t('platform.visitorId.status.qualified', 'Qualified')}</option>
                <option value="customer">{t('platform.visitorId.status.customer', 'Customer')}</option>
                <option value="ignored">{t('platform.visitorId.status.ignored', 'Ignored')}</option>
              </select>
            )}
            <button
              onClick={() => activeTab === 'companies' ? fetchCompanies() : fetchVisitors()}
              className="p-2 border rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        )}

        {/* Companies Tab */}
        {activeTab === 'companies' && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.company', 'Company')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.industry', 'Industry')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.visitors', 'Visitors')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.scores', 'Scores')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.status', 'Status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.lastSeen', 'Last Seen')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
                    </td>
                  </tr>
                ) : filteredCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      {t('platform.visitorId.noCompanies', 'No companies found. Add the tracking code to your website to start identifying visitors.')}
                    </td>
                  </tr>
                ) : (
                  filteredCompanies.map(company => (
                    <tr key={company.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => {
                      setSelectedCompany(company);
                      setShowCompanyModal(true);
                    }}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            {company.logo_url ? (
                              <img src={company.logo_url} alt="" className="h-10 w-10 rounded" />
                            ) : (
                              <div className="h-10 w-10 bg-gray-100 rounded flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{company.company_name}</div>
                            {company.domain && (
                              <div className="text-sm text-gray-500">{company.domain}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {company.industry || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{company.total_visitors} visitors</div>
                        <div className="text-xs text-gray-500">{company.total_visits} visits</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded ${getScoreColor(company.fit_score)}`}>
                            Fit: {company.fit_score}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded ${getScoreColor(company.intent_score)}`}>
                            Intent: {company.intent_score}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(company.status)}`}>
                          {company.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatTimeAgo(company.last_seen_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleAddToQueue(company.id, 'review');
                          }}
                          disabled={addingToQueue === company.id}
                          className={`p-1 rounded transition-colors ${
                            addedToQueue === company.id
                              ? 'text-green-600 bg-green-50'
                              : addingToQueue === company.id
                              ? 'text-gray-400'
                              : 'text-blue-600 hover:text-blue-900 hover:bg-blue-50'
                          }`}
                          title="Add to review queue"
                        >
                          {addingToQueue === company.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : addedToQueue === company.id ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Visitors Tab */}
        {activeTab === 'visitors' && (
          <>
            {/* Error/Notification Banner */}
            {visitorActionError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-red-700 text-sm flex-1">{visitorActionError}</p>
                <button
                  onClick={() => setVisitorActionError(null)}
                  className="text-red-500 hover:text-red-700"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>
            )}
            <div className="bg-white rounded-lg shadow overflow-hidden overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.visitor', 'Visitor')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.ipAddress', 'IP Address')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.device', 'Device')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.location', 'Location')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.company', 'Company')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.activity', 'Activity')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.engagement', 'Engagement')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.lastSeen', 'Last Seen')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('platform.visitorId.table.actions', 'Actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
                    </td>
                  </tr>
                ) : filteredVisitors.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      {t('platform.visitorId.noVisitors', 'No visitors found. Add the tracking code to your website to start identifying visitors.')}
                    </td>
                  </tr>
                ) : (
                  filteredVisitors.map(visitor => (
                    <tr key={visitor.id} className="hover:bg-gray-50">
                      {/* Visitor */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                            visitor.status === 'identified' ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            <Users className={`h-4 w-4 ${
                              visitor.status === 'identified' ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {visitor.status === 'identified' ? 'Identified' : 'Anonymous'}
                            </div>
                            <div className="text-xs text-gray-500 font-mono">
                              {visitor.visitor_fingerprint?.slice(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* IP Address */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {visitor.last_ip || visitor.last_ip_address || '—'}
                        </div>
                      </td>
                      {/* Device */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 capitalize">
                          {visitor.last_device_type || '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {[visitor.last_browser, visitor.last_os].filter(Boolean).join(' / ') || '—'}
                        </div>
                      </td>
                      {/* Location */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {visitor.last_geo_city || visitor.last_geo_country ? (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {[visitor.last_geo_city, visitor.last_geo_country].filter(Boolean).join(', ')}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      {/* Company */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        {visitor.company_name ? (
                          <div className="flex items-center gap-1">
                            <Building2 className="h-3 w-3 text-blue-500" />
                            <span className="text-sm font-medium text-blue-600">
                              {visitor.company_name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Unknown</span>
                        )}
                      </td>
                      {/* Activity */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{visitor.total_visits} visits</div>
                        <div className="text-xs text-gray-500">{visitor.total_page_views} pages</div>
                      </td>
                      {/* Engagement */}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-0.5 text-xs rounded ${getScoreColor(visitor.behavior_score)}`}>
                            B: {visitor.behavior_score}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded ${getScoreColor(visitor.intent_score)}`}>
                            I: {visitor.intent_score}
                          </span>
                        </div>
                      </td>
                      {/* Last Seen */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatTimeAgo(visitor.last_seen_at)}
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* View Details */}
                          <button
                            onClick={() => handleViewVisitorDetails(visitor)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {/* Lookup Company (if no company linked) */}
                          {!visitor.company_id && (visitor.last_ip || visitor.last_ip_address) && (
                            <button
                              onClick={() => handleLookupVisitorCompany(visitor)}
                              disabled={lookingUpVisitor === visitor.id}
                              className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors disabled:opacity-50"
                              title="Lookup company (WHOIS/DNS)"
                            >
                              {lookingUpVisitor === visitor.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Search className="h-4 w-4" />
                              )}
                            </button>
                          )}
                          {/* Add to Queue */}
                          <button
                            onClick={() => handleAddVisitorToQueue(visitor.id, 'review')}
                            disabled={addingVisitorToQueue === visitor.id}
                            className={`p-1.5 rounded transition-colors ${
                              addedVisitorToQueue === visitor.id
                                ? 'text-green-600 bg-green-50'
                                : addingVisitorToQueue === visitor.id
                                ? 'text-gray-400'
                                : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                            }`}
                            title="Add to queue"
                          >
                            {addingVisitorToQueue === visitor.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : addedVisitorToQueue === visitor.id ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <Plus className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Queue Tab */}
        {activeTab === 'queue' && (
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : queueItems.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <Target className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Queue is Empty</h3>
                <p className="text-gray-500">
                  Add companies from the Companies tab to start processing prospects.
                </p>
              </div>
            ) : (
              queueItems.map(item => (
                <div key={item.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 bg-gray-100 rounded flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-gray-400" />
                      </div>
                      <div>
                        <div className="font-medium">{item.company?.company_name || 'Unknown Company'}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            item.queue_type === 'prospect_create' ? 'bg-blue-100 text-blue-800' :
                            item.queue_type === 'enrich' ? 'bg-purple-100 text-purple-800' :
                            item.queue_type === 'review' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {item.queue_type.replace('_', ' ')}
                          </span>
                          <span>Priority: {item.priority}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleProcessQueueItem(item.id, 'complete')}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-1"
                      >
                        <CheckCircle className="h-4 w-4" /> Process
                      </button>
                      <button
                        onClick={() => handleProcessQueueItem(item.id, 'skip')}
                        className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1"
                      >
                        <XCircle className="h-4 w-4" /> Skip
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : analytics ? (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="text-sm text-gray-500">Total Visitors</div>
                    <div className="text-2xl font-bold text-gray-900">{analytics.total_visitors}</div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="text-sm text-gray-500">Companies</div>
                    <div className="text-2xl font-bold text-gray-900">{analytics.total_companies}</div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="text-sm text-gray-500">Identified</div>
                    <div className="text-2xl font-bold text-green-600">{analytics.identified_visitors}</div>
                  </div>
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="text-sm text-gray-500">Page Views</div>
                    <div className="text-2xl font-bold text-gray-900">{analytics.total_page_views}</div>
                  </div>
                </div>

                {/* Top Companies */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Top Companies by Visits</h3>
                  <div className="space-y-3">
                    {(analytics.top_companies || []).slice(0, 10).map((company, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-gray-900">{company.company_name}</span>
                        <span className="text-gray-500">{company.visits} visits</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Top Pages */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Top Pages</h3>
                  <div className="space-y-3">
                    {(analytics.top_pages || []).slice(0, 10).map((page, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-gray-900 truncate max-w-md">{page.page_url}</span>
                        <span className="text-gray-500">{page.views} views</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Analytics Data</h3>
                <p className="text-gray-500">
                  Start tracking visitors to see analytics.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Enrichment Tab */}
        {activeTab === 'enrichment' && (
          <div className="space-y-6">
            {/* IP Lookup Test */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <TestTube className="h-5 w-5" /> Test IP Enrichment
                </h3>
                <button
                  onClick={() => setShowIPLookupModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Search className="h-4 w-4" />
                  Lookup IP Address
                </button>
              </div>
              <p className="text-gray-600">
                Test the IP enrichment system by looking up any IP address. The system will attempt to identify the company using the three-tier waterfall approach.
              </p>
            </div>

            {/* Batch Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5" /> Batch Actions
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Run Batch Enrichment</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Enrich all visitors who haven't been processed in the last 24 hours.
                  </p>
                  <button
                    onClick={async () => {
                      const response = await visitorIdentificationApi.runBatchEnrichment(100);
                      if (response.success) {
                        alert(response.data?.message || 'Batch enrichment started');
                        fetchEnrichmentData();
                      }
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Start Batch Enrichment
                  </button>
                </div>
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Auto-Create Prospects</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Create prospect queue items from high-confidence enriched companies.
                  </p>
                  <button
                    onClick={async () => {
                      const response = await visitorIdentificationApi.autoCreateProspects({
                        min_confidence: 0.7,
                        min_visits: 3
                      });
                      if (response.success) {
                        alert(response.data?.message || 'Prospects created');
                        fetchQueue();
                      }
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    Create Prospects
                  </button>
                </div>
              </div>
            </div>

            {/* Provider Usage Dashboard */}
            {enrichmentAnalytics && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" /> Enrichment Analytics
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">Total Lookups</div>
                    <div className="text-2xl font-bold text-blue-600">{enrichmentAnalytics.total_lookups}</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">Success Rate</div>
                    <div className="text-2xl font-bold text-green-600">{enrichmentAnalytics.success_rate?.toFixed(1)}%</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">Avg Confidence</div>
                    <div className="text-2xl font-bold text-purple-600">{enrichmentAnalytics.average_confidence?.toFixed(1)}%</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600">Monthly Cost</div>
                    <div className="text-2xl font-bold text-orange-600">${enrichmentAnalytics.total_cost?.toFixed(2) || '0.00'}</div>
                  </div>
                </div>

                {/* Tier Breakdown */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="border rounded-lg p-4">
                    <div className="text-sm font-medium text-green-600 mb-2">Native Sources</div>
                    <div className="text-lg font-bold">{enrichmentAnalytics.native_hits || 0} hits</div>
                    <div className="text-xs text-gray-500">Free - Highest Priority</div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="text-sm font-medium text-blue-600 mb-2">Native Tools</div>
                    <div className="text-lg font-bold">{enrichmentAnalytics.tools_hits || 0} hits</div>
                    <div className="text-xs text-gray-500">Free - Medium Priority</div>
                  </div>
                  <div className="border rounded-lg p-4">
                    <div className="text-sm font-medium text-orange-600 mb-2">Third-Party Providers</div>
                    <div className="text-lg font-bold">{enrichmentAnalytics.third_party_hits || 0} hits</div>
                    <div className="text-xs text-gray-500">Paid - Lowest Priority</div>
                  </div>
                </div>
              </div>
            )}

            {/* Enrichment Providers Configuration */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5" /> Enrichment Providers
              </h3>

              {/* Tier 1: Native Sources */}
              <div className="mb-6">
                <h4 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                  <Database className="h-4 w-4" /> Tier 1: Native Sources (Free)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {enrichmentProviders
                    .filter(p => p.tier === 'native')
                    .map(provider => (
                      <div key={provider.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium">{provider.display_name}</div>
                          <div className="flex items-center gap-2">
                            {provider.is_enabled ? (
                              <span className="text-green-600 text-sm flex items-center gap-1">
                                <ToggleRight className="h-4 w-4" /> Enabled
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm flex items-center gap-1">
                                <ToggleLeft className="h-4 w-4" /> Disabled
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 mb-2">
                          Priority: {provider.priority} | Confidence Threshold: {provider.confidence_threshold}%
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>Requests today: {provider.today_requests}</span>
                          <button
                            onClick={() => {
                              setSelectedProvider(provider);
                              setShowProviderModal(true);
                            }}
                            className="text-blue-600 hover:underline"
                          >
                            Configure
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Tier 2: Native Tools */}
              <div className="mb-6">
                <h4 className="font-medium text-blue-700 mb-3 flex items-center gap-2">
                  <Network className="h-4 w-4" /> Tier 2: Native Tools (Free)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {enrichmentProviders
                    .filter(p => p.tier === 'native_tools')
                    .map(provider => (
                      <div key={provider.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium">{provider.display_name}</div>
                          <div className="flex items-center gap-2">
                            {provider.is_enabled ? (
                              <span className="text-green-600 text-sm flex items-center gap-1">
                                <ToggleRight className="h-4 w-4" /> Enabled
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm flex items-center gap-1">
                                <ToggleLeft className="h-4 w-4" /> Disabled
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 mb-2">
                          Priority: {provider.priority} | Confidence Threshold: {provider.confidence_threshold}%
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>Requests today: {provider.today_requests}</span>
                          <button
                            onClick={() => {
                              setSelectedProvider(provider);
                              setShowProviderModal(true);
                            }}
                            className="text-blue-600 hover:underline"
                          >
                            Configure
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Tier 3: Third-Party Providers */}
              <div>
                <h4 className="font-medium text-orange-700 mb-3 flex items-center gap-2">
                  <Server className="h-4 w-4" /> Tier 3: Third-Party Providers (Paid)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {enrichmentProviders
                    .filter(p => p.tier === 'third_party')
                    .map(provider => (
                      <div key={provider.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium">{provider.display_name}</div>
                          <div className="flex items-center gap-2">
                            {provider.api_key_set ? (
                              <span className="text-green-600 text-xs flex items-center gap-1">
                                <Key className="h-3 w-3" /> API Key Set
                              </span>
                            ) : (
                              <span className="text-yellow-600 text-xs flex items-center gap-1">
                                <Key className="h-3 w-3" /> No API Key
                              </span>
                            )}
                            {provider.is_enabled ? (
                              <span className="text-green-600 text-sm flex items-center gap-1">
                                <ToggleRight className="h-4 w-4" /> Enabled
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm flex items-center gap-1">
                                <ToggleLeft className="h-4 w-4" /> Disabled
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 mb-2">
                          Priority: {provider.priority} | Cost: ${provider.cost_per_request?.toFixed(3) || '0.00'}/request
                        </div>
                        <div className="text-sm text-gray-500 mb-2">
                          Budget: ${provider.current_month_cost?.toFixed(2) || '0.00'} / ${provider.monthly_budget?.toFixed(2) || '∞'}
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>
                            {provider.current_month_requests} / {provider.monthly_request_limit || '∞'} this month
                          </span>
                          <button
                            onClick={() => {
                              setSelectedProvider(provider);
                              setShowProviderModal(true);
                            }}
                            className="text-blue-600 hover:underline"
                          >
                            Configure
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Manual IP Mappings */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MapPin className="h-5 w-5" /> Manual IP Mappings
                </h3>
                <button
                  onClick={() => setShowAddMappingModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Mapping
                </button>
              </div>
              <p className="text-gray-600 mb-4">
                Override automatic IP enrichment by manually mapping specific IP addresses or ranges to companies.
              </p>

              {ipMappings.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {ipMappings.map(mapping => (
                      <tr key={mapping.id}>
                        <td className="px-4 py-3 text-sm font-mono">{mapping.ip_address}</td>
                        <td className="px-4 py-3 text-sm">{mapping.company_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{mapping.notes || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatTimeAgo(mapping.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleDeleteIPMapping(mapping.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No manual IP mappings configured</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Setup Tab */}
        {activeTab === 'setup' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Code className="h-5 w-5" /> Tracking Code Installation
              </h3>
              <p className="text-gray-600 mb-4">
                Add the tracking code to your website to start identifying visitors and companies.
              </p>
              <button
                onClick={() => setShowSnippetModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Get Tracking Code
              </button>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5" /> IP Enrichment
              </h3>
              <p className="text-gray-600 mb-4">
                IP enrichment uses a hybrid approach: native WeaveIn sources first, then third-party providers if needed.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <div className="font-medium">Native Sources (Free)</div>
                    <div className="text-sm text-gray-600">CBO network matching, form correlation, partner intelligence</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-medium">Native Tools (Free)</div>
                    <div className="text-sm text-gray-600">Reverse DNS, WHOIS lookup, ASN resolution</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="font-medium">Third-Party Providers (Optional)</div>
                    <div className="text-sm text-gray-600">Clearbit, ZoomInfo, IPinfo - configure API keys to enable</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <TrackingSnippetModal
        isOpen={showSnippetModal}
        onClose={() => setShowSnippetModal(false)}
      />
      <CompanyDetailModal
        isOpen={showCompanyModal}
        onClose={() => {
          setShowCompanyModal(false);
          setSelectedCompany(null);
        }}
        company={selectedCompany}
        onStatusChange={handleStatusChange}
        onAddToQueue={handleAddToQueue}
        onCompanyUpdate={(updatedCompany) => {
          // Update company in local state
          setCompanies(prev => prev.map(c => c.id === updatedCompany.id ? updatedCompany : c));
          setSelectedCompany(updatedCompany);
        }}
      />
      <EnrichmentProviderModal
        isOpen={showProviderModal}
        onClose={() => {
          setShowProviderModal(false);
          setSelectedProvider(null);
        }}
        provider={selectedProvider}
        onSave={handleSaveProvider}
      />
      <IPLookupModal
        isOpen={showIPLookupModal}
        onClose={() => setShowIPLookupModal(false)}
      />
      <AddIPMappingModal
        isOpen={showAddMappingModal}
        onClose={() => setShowAddMappingModal(false)}
        companies={companies}
        onSave={handleAddIPMapping}
      />
      <VisitorDetailModal
        isOpen={showVisitorModal}
        onClose={() => {
          setShowVisitorModal(false);
          setSelectedVisitor(null);
        }}
        visitor={selectedVisitor}
        onAddToQueue={handleAddVisitorToQueue}
        onLookupCompany={handleLookupVisitorCompany}
      />
      <EnrichmentReviewModal
        isOpen={showEnrichmentReviewModal}
        onClose={() => {
          setShowEnrichmentReviewModal(false);
          setEnrichmentReviewVisitor(null);
          setEnrichmentResult(null);
        }}
        visitor={enrichmentReviewVisitor}
        enrichmentResult={enrichmentResult}
        onAccept={handleLinkVisitorToCompany}
        onSkip={handleSkipEnrichmentReview}
      />
    </div>
  );
}

export default VisitorIdentificationPage;
